import axiosInstance from '../axiosConfig';
import {
  enqueueOfflineOperation,
  upsertOfflineOperation,
  OfflineOperationMethod,
} from '../hooks/useOfflineQueue';

interface MutateWithOfflineOptions<TSimulated> {
  entityType: string;
  simulateResponse?: (params: {
    url: string;
    body: any;
    tempId: string;
  }) => TSimulated;

  // Jeśli podane, to zamiast enqueue robimy upsert po dedupeKey
  dedupeKey?: string;
  
  forceOffline?: boolean;
}

export const mutateWithOffline = async <TResponse = any, TSimulated = TResponse>(
  method: OfflineOperationMethod,
  url: string,
  body: any,
  options: MutateWithOfflineOptions<TSimulated>
): Promise<TResponse> => {
  const isFormData =
    typeof FormData !== 'undefined' && body instanceof FormData;

  // W PWA/SW zdarza się, że navigator.onLine zwraca true, a requesty i tak
  // lecą w offline (503 z SW). Dla payloadów binarnych/multipart wolimy zawsze
  // kolejkę offline (upload mamy obsłużony osobno).
  const hasBinaryLikePayload =
    !isFormData &&
    body &&
    typeof body === 'object' &&
    (body.drawing instanceof Blob ||
      body.drawing instanceof File ||
      (Array.isArray(body.file) &&
        body.file.some((f: any) => f instanceof Blob || f instanceof File)));

  const isEffectiveOnline =
    navigator.onLine && !options.forceOffline && !isFormData && !hasBinaryLikePayload;

  if (isEffectiveOnline) {
    const response = await axiosInstance.request<TResponse>({
      method,
      url,
      data: body,
    });
    return response.data;
  }

  const tempId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? // @ts-ignore
        (crypto.randomUUID() as string)
      : `offline-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  if (options.dedupeKey) {
    upsertOfflineOperation({
      method,
      url,
      body,
      entityType: options.entityType,
      tempId,
      dedupeKey: options.dedupeKey,
    });
  } else {
    enqueueOfflineOperation({
      method,
      url,
      body,
      entityType: options.entityType,
      tempId,
    });
  }

  if (options.simulateResponse) {
    const simulated = options.simulateResponse({ url, body, tempId });
    return simulated as unknown as TResponse;
  }

  const fallback: any = {
    status: 'pending-offline',
    tempId,
  };

  return fallback as TResponse;
};
